import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// ✅ FIXED BASE URL (no /api here)
const BASE_URL =
  process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5000";

// ✅ API ROOT
const API = `${BASE_URL}/availability/alternative`;

// ================= HELPER =================
const getData = (res) => res?.data?.data || res?.data;

// ================= ERROR HELPER =================
const getError = (err) =>
  err?.response?.data?.message || err?.message || "Something went wrong";

// ================= SAVE =================
export const saveAlternativeAvailability = createAsyncThunk(
  "alternative/save",
  async ({ data, token }, { rejectWithValue }) => {
    try {
      const res = await axios.post(API, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return getData(res);
    } catch (err) {
      return rejectWithValue(getError(err));
    }
  },
);

// ================= GET =================
export const getAlternativeAvailability = createAsyncThunk(
  "alternative/getMine",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return getData(res);
    } catch (err) {
      return rejectWithValue(getError(err));
    }
  },
);

// ================= CHECK =================
export const checkAlternativeAvailability = createAsyncThunk(
  "alternative/check",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API}/check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return getData(res); // ✅ FIXED (consistent)
    } catch (err) {
      return rejectWithValue(getError(err));
    }
  },
);

// ================= WEEKLY =================
export const getWeeklyOpenSlots = createAsyncThunk(
  "alternative/weekly",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API}/open-slots/weekly`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return getData(res)?.openSlots || []; // ✅ FIXED
    } catch (err) {
      return rejectWithValue(getError(err));
    }
  },
);

// ================= UPDATE DAY =================
export const updateAlternativeDay = createAsyncThunk(
  "alternative/updateDay",
  async ({ data, token }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${API}/day`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return getData(res);
    } catch (err) {
      return rejectWithValue(getError(err));
    }
  },
);

// ================= UPDATE SLOT =================
export const updateAlternativeSlot = createAsyncThunk(
  "alternative/updateSlot",
  async ({ data, token }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${API}/slot`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return getData(res);
    } catch (err) {
      return rejectWithValue(getError(err));
    }
  },
);

// ================= CLEAR WEEK =================
export const clearAlternativeWeek = createAsyncThunk(
  "alternative/clearWeek",
  async ({ token }, { rejectWithValue }) => {
    try {
      await axios.delete(`${API}/clear-week`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return true;
    } catch (err) {
      return rejectWithValue(getError(err));
    }
  },
);

// ================= DELETE =================
export const deleteAlternativeAvailability = createAsyncThunk(
  "alternative/deleteAll",
  async ({ token }, { rejectWithValue }) => {
    try {
      await axios.delete(API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return true;
    } catch (err) {
      return rejectWithValue(getError(err));
    }
  },
);

// ================= SLICE =================
const alternativeSlice = createSlice({
  name: "alternativeAvailability",
  initialState: {
    single: null,
    list: [],
    check: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // ✅ SUCCESS CASES
      .addCase(saveAlternativeAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.single = action.payload;
      })

      .addCase(getAlternativeAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.single = action.payload;
      })

      .addCase(checkAlternativeAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.check = action.payload;
      })

      .addCase(getWeeklyOpenSlots.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })

      .addCase(updateAlternativeDay.fulfilled, (state, action) => {
        state.loading = false;
        state.single = action.payload;
      })

      .addCase(updateAlternativeSlot.fulfilled, (state, action) => {
        state.loading = false;
        state.single = action.payload;
      })

      .addCase(clearAlternativeWeek.fulfilled, (state) => {
        state.loading = false;
        if (state.single?.weeklyAvailability) {
          state.single.weeklyAvailability = [];
        }
      })

      .addCase(deleteAlternativeAvailability.fulfilled, (state) => {
        state.loading = false;
        state.single = null;
        state.list = [];
      })

      // ✅ GLOBAL PENDING
      .addMatcher(
        (action) =>
          action.type.startsWith("alternative/") &&
          action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = null;
        },
      )

      // ✅ GLOBAL ERROR
      .addMatcher(
        (action) =>
          action.type.startsWith("alternative/") &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || "Request failed";
        },
      );
  },
});

export const { clearError } = alternativeSlice.actions;
export default alternativeSlice.reducer;
